import { stringfyArrayItems } from 'app/utils';
import { Box, Typography } from 'components';
import { Flex } from 'components/flex';
import { IconsTypes } from 'components/icons';
import Icons from 'components/icons/Icons';
import { ExamsViews } from 'features/exams';
import { useNavigate } from 'react-router-dom';
import styled, { ColorsTypes, css } from 'styled-components';

interface CardWrapperProps
  extends Partial<{
    view: ExamsViews;
    titleColor: ColorsTypes;
    buttonBackground: ColorsTypes;
    buttonBackgroundHover: ColorsTypes;
    buttonColor: ColorsTypes;
    buttonColorHover: ColorsTypes;
    borderColor: ColorsTypes;
    borderColorHover: ColorsTypes;
    background: ColorsTypes;
    backgroundHover: ColorsTypes;
    showOverlay: boolean;
    cursor: React.CSSProperties['cursor'];
    buttonIconColor: ColorsTypes;
    buttonIconColorHover: ColorsTypes;
  }> {}

const CardSearchButton = styled.button`
  transition: all 0.2s ease;
  position: relative;
  border: 0;
  padding: 0 16px;
  text-align: left;
  outline: none;
  border-radius: 0 0 4px 4px;
`;

const CardSearchButtonLabel = styled.span`
  ${({ theme }) => theme.fontTypes.type3}
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-weight: 600;
`;

const CardSearchButtonIcon = styled(Icons)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  right: 15px;
`;

const CardSiding = styled(Flex)`
  transition: all 5s ease;
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.gray_600};
  backdrop-filter: blur(6px);
  z-index: 10;
  display: none;
  padding: 0 15px;
  align-items: center;
  cursor: not-allowed;
  border-radius: 5px;
`;

const CardIcon = styled(Icons)`
  width: 24px;
  color: ${(props: any) => props.theme.colors[props['data-color']]};
`;

const CardWrapper = styled(Flex)<CardWrapperProps>`
  border: 1px solid ${({ theme }) => theme.colors.gray_300};
  border-radius: 5px;
  justify-content: space-between;
  position: relative;
  max-width: 280px;
  min-height: 400px;
  user-select: none;

  ${({ theme, view = 'grid' }) => {
    if (view === 'grid') {
      return css`
        flex-direction: column;
        width: 278px;
        min-height: 340px;

        ${CardSearchButton} {
          height: 50px;
        }
      `;
    }

    return css`
      flex-direction: row;
      min-height: 65px;

      ${CardSearchButton} {
        flex-shrink: 0;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;

        ${theme.mediaQueries.mobile} {
          width: 50px;

          ${CardSearchButtonLabel} {
            display: none;
          }
        }

        ${theme.mediaQueries.tablet} {
          width: 250px;

          ${CardSearchButtonLabel} {
            display: inline-block;
          }
        }
      }
    `;
  }}

  ${({
    theme,
    background = 'white',
    backgroundHover = background,

    buttonColor = 'black',
    buttonColorHover = buttonColor,

    buttonBackground = 'white',
    buttonBackgroundHover = buttonBackground,

    buttonIconColor = 'primary_400',
    buttonIconColorHover = buttonIconColor,

    borderColor,
    borderColorHover = borderColor,

    showOverlay,
    cursor = 'pointer',
  }) =>
    css`
      ${!borderColor &&
      css`
        border: none;
      `}

      background: ${theme.colors[background as ColorsTypes]};
      border-color: ${theme.colors[borderColor as ColorsTypes]};

      ${CardSearchButton} {
        color: ${theme.colors[buttonColor as ColorsTypes]};
        background: ${theme.colors[buttonBackground as ColorsTypes]};
        cursor: ${cursor};
      }

      ${CardSearchButtonIcon} {
        color: ${theme.colors[buttonIconColor as ColorsTypes]};
      }

      &:hover {
        border-color: ${theme.colors[borderColorHover as ColorsTypes]};
        background: ${theme.colors[backgroundHover]};

        ${CardSearchButton} {
          color: ${theme.colors[buttonColorHover as ColorsTypes]};
          background: ${theme.colors[buttonBackgroundHover as ColorsTypes]};
        }

        ${CardSearchButtonIcon} {
          color: ${theme.colors[buttonIconColorHover as ColorsTypes]};
        }

        ${CardSiding} {
          display: ${showOverlay ? 'flex' : 'revert'};
        }
      }
    `};
`;

export interface CardSearchProps extends CardWrapperProps {
  title: string;
  titleLabel: string;
  titleColor?: ColorsTypes;

  subtitle: string;
  subtitleLabel: string;
  subtitleColor?: ColorsTypes;

  buttonLabel: string;
  buttonIcon?: IconsTypes;
  topIcon?: IconsTypes;
  topIconColor?: ColorsTypes;
  unavailableMessageHeader?: string;
  unavailableMessage?: string | string[];
  link?: string;
}

const CardSearch = ({
  title,
  titleLabel,
  subtitle,
  subtitleLabel,
  buttonLabel,
  view = 'grid',
  titleColor = 'black',
  subtitleColor = 'black',
  topIcon,
  topIconColor = 'primary_400',
  buttonIcon,
  showOverlay,
  unavailableMessageHeader = 'Disponível para:',
  unavailableMessage,
  link,
  ...rest
}: CardSearchProps) => {
  const navigate = useNavigate();

  const handleOnClick = () => (link ? navigate(link) : {});

  return (
    <CardWrapper view={view} flexWrap="nowrap" showOverlay={showOverlay} {...rest}>
      {showOverlay && (
        <CardSiding>
          <Flex textAlign="center" flexDirection="column">
            <Typography as="span" type="type2" color="white" fontWeight="bold" upper>
              {unavailableMessageHeader}
            </Typography>

            <Typography as="span" type="type1" color="white" lineHeight={1.3} mt={view === 'grid' ? 2 : 0}>
              {Array.isArray(unavailableMessage) ? stringfyArrayItems(unavailableMessage) : unavailableMessage}
            </Typography>
          </Flex>
        </CardSiding>
      )}

      <Flex flexGrow={1} flexDirection="column" justifyContent="space-between">
        <Flex flexGrow={0} justifyContent="space-between" flexWrap="nowrap">
          <Flex flexGrow={1} flexDirection={view === 'grid' ? 'column' : { m: 'column', t: 'row' }} justifyContent="space-between">
            <Flex
              flexGrow={0}
              flexDirection={view === 'grid' ? { m: 'row' } : { m: 'column', t: 'row' }}
              px={view === 'grid' ? { m: 3 } : { m: 3 }}
              pt={view === 'grid' ? { m: 3 } : { m: 2 }}
            >
              {!!subtitle && (
                <Typography
                  as="span"
                  type={view === 'grid' ? 'type3' : 'type1'}
                  fontWeight="bold"
                  color={subtitleColor}
                  mr={{ m: 0, t: 3 }}
                  mb={{ m: 1 }}
                >
                  {subtitle}
                </Typography>
              )}

              {view === 'list' && !!subtitleLabel && (
                <Typography as="span" type="type1" color={subtitleColor}>
                  {subtitleLabel}
                </Typography>
              )}
            </Flex>

            {view === 'list' && !!titleLabel && (
              <Box flexGrow={0} px={{ m: 2, t: 3 }}>
                <Typography as="span" type="type0" color={subtitleColor}>
                  {titleLabel}
                </Typography>
              </Box>
            )}

            {view === 'grid' && !!subtitleLabel && (
              <Flex flexGrow={1} px={{ m: 3 }}>
                <Typography as="span" type="type0" color={subtitleColor}>
                  {subtitleLabel}
                </Typography>
              </Flex>
            )}
          </Flex>

          {topIcon && view === 'grid' && (
            <Flex flexGrow={0} alignItems="start" pt={3} pr={3}>
              <CardIcon icon="fileDocumentOutline" data-color={topIconColor} />
            </Flex>
          )}
        </Flex>

        <Flex flexGrow={0} flexDirection="column" px={view === 'grid' ? { m: 3 } : { m: 3 }} pb={view === 'grid' ? { m: 3 } : { m: 2 }} mr={4}>
          {view === 'grid' && !!titleLabel && (
            <Typography as="span" type={view === 'grid' ? 'type1' : 'type0'} color={subtitleColor} mb={1}>
              {titleLabel}
            </Typography>
          )}

          <Typography
            as="span"
            color={titleColor}
            lineHeight={1.1}
            mt={view === 'grid' ? { m: 0, t: 1 } : { m: 0, t: 0 }}
            type={view === 'grid' ? 'type6' : 'type5'}
            fontWeight={view === 'grid' ? 'medium' : 'regular'}
          >
            {title}
          </Typography>
        </Flex>
      </Flex>

      <CardSearchButton onClick={handleOnClick}>
        <CardSearchButtonLabel>{buttonLabel}</CardSearchButtonLabel>
        {buttonIcon && <CardSearchButtonIcon icon={buttonIcon} />}
      </CardSearchButton>
    </CardWrapper>
  );
};

export default CardSearch;
